import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { capitalize, intersection, sortBy } from 'lodash';
import BreakLine from 'components/BreakLine';
import PayRateElement from 'components/PayRateElement';
import { ThemeProvider } from 'react-css-themr';
import { Tab, Tabs } from 'react-toolbox';
import RTTabs from 'styles/tabs';
import payRatesStyle from './payRates.scss';
import UserDetailsCard from 'components/users/UserDetailsCard';
import UserDetailsBasicInfo from 'components/users/UserDetailsBasicInfo';
import UserDetailsBadgeInfo from 'components/users/UserDetailsBadgeInfo';
import UserDetailsRow from 'components/users/UserDetailsRow';
import UserDetailsElement from 'components/users/UserDetailsElement';
import EmployableHistory from '../../../../../shared/components/EmployableHistory';
import { stringifyList } from '../../../../../utils';
import DetailsAddress from 'components/DetailsAddress';
import EstimatorType, { RoleType } from 'types/user';
import TimeOffTable from 'components/TimeOffTable';
import PTOType from 'types/pto';
import WorkroomType from 'types/workroom';
import { EditToggleButton } from 'components/EditableTable';
import { can } from '../../../../../services';
import EstimatorCoveredZips from '../components/estimatorCoveredZips';
import Moment from 'moment';

const detailsTheme = { RTTabs };

class EstimatorDetailsCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditable: false,
      timeOffTab: can(props.role, 'view', 'pay_rate') ? 2 : 1,
    };
  }

  getStoreName = (storeId) => {
    const store = this.props.allStores.find(s => s.id === storeId);
    return `${store.name} - ${store.number} Availability`;
  };

  getDaysString = (period = []) => (period.length ?
    period.map(d => capitalize(d)).join(', ') : 'N/A');

  getAvailabilitiesByStore = availabilities => (availabilities || []).filter((a) => {
    const { estimator: { covered_zip_codes = [] } } = this.props;
    const store = this.props.allStores.find(s => s.id === a.assignee_availability.store_id);
    const storeZips = store ? store.covered_zip_codes : [];
    return (storeZips || []).length && (covered_zip_codes || []).length &&
      intersection(storeZips, covered_zip_codes).length;
  }).map(a => a.assignee_availability);

  handleEditToggle = () => this.setState({ isEditable: !this.state.isEditable });

  isEstimator = () => this.props.role === 'estimator';

  renderTimeOffEditButton = () => {
    const { role, user, estimator } = this.props;
    if (!can(role, 'manage', 'pto', { user_id: user.id, obj_id: estimator.user_id })) return null;
    return <EditToggleButton actionLabel="Edit Time Off" isEditable={this.state.isEditable} onClick={this.handleEditToggle} />;
  };

  renderAvailabilityByStore = (stores) => {
    if (!stores.length) return null;
    const filteredStores = [];
    sortBy(stores, ['id']).forEach((s1) => {
      if (!filteredStores.length ||
          filteredStores.every(s2 => s2.store_id !== s1.store_id)) filteredStores.push(s1);
    });
    return filteredStores.map((s) => {
      const { id, store_id, morning, afternoon, evening } = s;
      return (<UserDetailsRow key={id} title={this.getStoreName(store_id)}>
        <UserDetailsElement
          wrap
          title="Morning"
          value={this.getDaysString(morning)}
        />
        <UserDetailsElement
          wrap
          title="Afternoon"
          value={this.getDaysString(afternoon)}
        />
        <UserDetailsElement
          wrap
          title="Evening"
          value={this.getDaysString(evening)}
        />
      </UserDetailsRow>);
    });
  }

  render = () => {
    const { role, workrooms, activeTab, onTabChange,
            handleRateHistory, ptos, estimator, estimator: {
              assignee_availabilities = [], address = '', city = '', state = '',
              zip_code = '', covered_zip_codes = [], shared_zip_map = {},
              floor_wizard_ident = '', trusted = false, is_self_schedulable = false,
              spig_employee = false, lrrp_training_expiration = '',
              workers_comp_expiration = '', comments = '',
              assigned_supervisor = null, supervisor_name = null,
              liability_insurance_expiration = '', per_assessment_rate = '',
              mileage_rate = '', trainee_rate = '', training_bonus = '',
              estimator_start_date = '', estimator_end_date = '', off_review_date = '',
              morning_max_appts = 0, afternoon_max_appts = 0, evening_max_appts = 0,
            } } = this.props;
    const { position = '', manage_vendor_workrooms: mvw = {} } = this.props.user || {};
    const supervisorDisplayName =
      (assigned_supervisor && assigned_supervisor.name) || supervisor_name;
    const duration = estimator_end_date != null ?
    Moment.duration(Moment(estimator_end_date).diff(Moment(estimator_start_date))) :
    Moment.duration(Moment().diff(Moment(estimator_start_date)));
    /* eslint no-underscore-dangle: 0 */
    const years = duration._data.years;
    const months = duration._data.months;
    const days = duration._data.days;
    const userProps = {
      user: estimator,
      workrooms,
      type: 'estimators',
      role,
      resource: 'estimator',
      editHandler: activeTab === this.state.timeOffTab ? this.renderTimeOffEditButton() : null,
    };
    const stores = this.getAvailabilitiesByStore(assignee_availabilities);
    return (<ThemeProvider theme={detailsTheme}>
      <UserDetailsCard {...userProps} >
        <Tabs index={activeTab} onChange={onTabChange} className={RTTabs.absoluteTabs}>
          <Tab label="Basic Info">
            <UserDetailsBasicInfo user={estimator} />
            <UserDetailsBadgeInfo user={estimator} type="estimator" />
            <UserDetailsRow title="Address">
              <UserDetailsElement title="street address">
                <DetailsAddress
                  address={address}
                  city={city}
                  state={state}
                  zipCode={zip_code}
                />
              </UserDetailsElement>
            </UserDetailsRow>
            <UserDetailsRow title="Estimator Info">
              <UserDetailsElement
                wrap
                title="workroom(s)"
                value={stringifyList(workrooms)}
              />
              <UserDetailsElement wrap title="zip codes">
                <EstimatorCoveredZips
                  covered_zip_codes={covered_zip_codes}
                  shared_zip_map={shared_zip_map}
                  estimator={estimator}
                  role={role}
                />
              </UserDetailsElement>
              {!this.isEstimator() && (<Fragment>
                <UserDetailsElement
                  small
                  title="floor wizard id"
                  value={floor_wizard_ident || 'N/A'}
                />
                <UserDetailsElement
                  small
                  title="Bypass Review, Send Directly to Store"
                  value={trusted ? 'Yes' : 'No'}
                />
                <UserDetailsElement
                  small
                  title="Self Schedulable"
                  value={is_self_schedulable ? 'Yes' : 'No'}
                />
                <UserDetailsElement
                  small
                  title="SPIG Employee"
                  value={spig_employee ? 'Yes' : 'No'}
                />
                <UserDetailsElement
                  title="LRRP training expiration"
                  value={lrrp_training_expiration || 'N/A'}
                />
                <UserDetailsElement
                  title="Off Review Date"
                  value={off_review_date || 'N/A'}
                />
                <UserDetailsElement
                  title="Tenure"
                  value={`${years ? `${years} years,` : ''} ${months ? `${months} months,` : ''} ${days ? `${days} days` : ''}`}
                />
              </Fragment>)}
            </UserDetailsRow>
            {!this.isEstimator() && (<UserDetailsRow>
              <UserDetailsElement title="Notes/Comments" value={comments || 'N/A'} />
            </UserDetailsRow>)}
            <UserDetailsRow title="Daily Appointment Limits">
              <UserDetailsElement title="Morning" value={morning_max_appts} />
              <UserDetailsElement title="Afternoon" value={afternoon_max_appts} />
              <UserDetailsElement title="Evening" value={evening_max_appts} />
            </UserDetailsRow>
            {(workrooms || []).length &&
             (assignee_availabilities || []).length &&
             (stores || []).length ? this.renderAvailabilityByStore(stores) :
             <UserDetailsRow title="Availability By Store">No Availabilities Set</UserDetailsRow>}
            <UserDetailsRow title="Insurance">
              <UserDetailsElement
                title="worker's comp. expiration"
                value={workers_comp_expiration}
              />
              <UserDetailsElement
                title="general liability insurance expiration"
                value={liability_insurance_expiration}
              />
            </UserDetailsRow>
            {can(role, 'view', 'SUPERVISOR_INFO', { position, mvw }) && supervisorDisplayName && (
              // "Supervisor" is labeled as "Coordinator" in the UI since
              // that's more familiar to IFI users, but the value is still
              // stored as "supervisor_id" in the backend.
              <UserDetailsRow title="Coordinator">
                <UserDetailsElement
                  title="Assigned Coordinator"
                  value={supervisorDisplayName}
                />
              </UserDetailsRow>
            )}
          </Tab>
          {(can(role, 'view', 'pay_rate') && <Tab label="Pay Rate">
            <div className={payRatesStyle.payRateContainer}>
              <PayRateElement
                component={UserDetailsElement}
                title="per assessment rate"
                rate={per_assessment_rate}
                handleHistory={() => handleRateHistory('per_assessment_rate')}
              />
              <PayRateElement
                component={UserDetailsElement}
                title="mileage rate"
                rate={mileage_rate}
                handleHistory={() => handleRateHistory('mileage_rate')}
              />
              <BreakLine />
            </div>
            <div className={payRatesStyle.payRateContainer}>
              <PayRateElement
                component={UserDetailsElement}
                title="trainee rate"
                rate={trainee_rate}
                handleHistory={() => handleRateHistory('trainee_rate')}
                mobile="2x"
              />
              <PayRateElement
                component={UserDetailsElement}
                title="training bonus"
                rate={training_bonus}
                handleHistory={() => handleRateHistory('training_bonus')}
                mobile="2x"
              />
              <BreakLine />
            </div>
          </Tab>) || <div />}
          <Tab label="Time Off">
            <TimeOffTable
              isEditable={this.state.isEditable}
              ptos={ptos}
              parentID={estimator.id}
              parentKind="estimators"
              parentObj={estimator}
            />
          </Tab>
          {!this.isEstimator() && <Tab label="History">
            <EmployableHistory employable={estimator} employableType={'estimator'} />
          </Tab>}
        </Tabs>
      </UserDetailsCard>
    </ThemeProvider>);
  };
}

EstimatorDetailsCard.propTypes = {
  estimator: EstimatorType,
  role: PropTypes.string,
  workrooms: PropTypes.arrayOf(WorkroomType).isRequired,
  allStores: PropTypes.array,
  activeTab: PropTypes.number,
  onTabChange: PropTypes.func,
  onResend: PropTypes.func,
  handleRateHistory: PropTypes.func,
  ptos: PropTypes.arrayOf(PTOType),
  user: RoleType,
};

export default EstimatorDetailsCard;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/estimators/components/estimatorDetailsCard.jsx